import {
  ApiError,
  Individ,
  NO_CONNECTION_STATUS_CODE,
  NOT_AUTHENTICATED_STATUS_CODES,
} from "~/src/api/types";
import { jaktdataApi } from "~/src/appGlobals";
import { isDeletedOnServer, syncStatusForError } from "~/src/contexts/AppDataContext/syncHelpers";
import { toErrorRecords } from "~/src/helpers/formErrors";
import { postIndividQuery, putIndividQuery } from "~/src/react-query/queries";
import { AppStorage, StorageItem } from "../StorageContext/StorageProvider";

const isUnrecoverable = (e: ApiError) => {
  const unrecoverableErrors = ["Jaktdagen er godkjent og skutte dyr kan dermed ikke legges til."];
  return e.statusCode == 400 && unrecoverableErrors.includes(e.message);
};

export const syncIndivider = async (storage: AppStorage) => {
  const pendingIndivider = storage
    .getAll<Individ>("Individ")
    .filter((i) => i.syncStatus != "unconfirmed");
  if (pendingIndivider.length === 0) return;

  const toCreate = pendingIndivider.filter((individ) => individ.operation === "create");
  const toUpdate = pendingIndivider.filter((individ) => individ.operation === "update");
  const toDelete = pendingIndivider.filter((individ) => individ.operation === "delete");

  let individerSynced = 0;
  let individerNotSynced = 0;

  let createRequests: Promise<void>[] = [];
  let updateRequests: Promise<void>[] = [];
  let deleteRequests: Promise<void>[] = [];

  const syncConfirm = (individ: StorageItem<Individ>) => {
    individerSynced += 1;
    storage.add({
      ...individ,
      syncStatus: "unconfirmed",
      errors: undefined,
      isPending: false,
      lastUpdated: new Date(),
    });
  };

  const syncReject = async (individ: StorageItem<Individ>, e: ApiError) => {
    if (isUnrecoverable(e)) {
      individerNotSynced += 1;
      storage.delete(individ.objectKey, "Individ");
    } else if (!NOT_AUTHENTICATED_STATUS_CODES.includes(e.statusCode)) {
      if (e.statusCode === NO_CONNECTION_STATUS_CODE) {
        //timeout error, add back into queue
        storage.add({
          ...individ,
          syncStatus: "pending",
          errors: undefined,
        });
      } else if (
        individ.operation === "delete" &&
        (e.message.includes("Individ med Id må finnes i databasen") ||
          e.message.includes("Individ med Id ble ikke funnet"))
      ) {
        //trying to delete something that does not exist
        storage.delete(individ.objectKey, "Individ");
      } else {
        //if the individ has been created already on the server, we try update instead
        await Promise.all([
          putIndividQuery(individ.data)
            .then(() => {
              syncConfirm(individ);
            })
            .catch(() => {
              individerNotSynced += 1;
              storage.add({
                ...individ,
                syncStatus: syncStatusForError(e),
                errors: toErrorRecords(e),
              });
            }),
        ]);
      }
    } else {
      individerNotSynced += 1;
      storage.add({
        ...individ,
        syncStatus: syncStatusForError(e),
        errors: toErrorRecords(e),
      });
    }
  };

  for (const individ of toCreate) {
    createRequests.push(
      postIndividQuery(individ.data)
        .then(() => syncConfirm(individ))
        .catch((e: ApiError) => syncReject(individ, e)),
    );
  }

  for (const individ of toUpdate) {
    updateRequests.push(
      putIndividQuery(individ.data)
        .then(() => syncConfirm(individ))
        .catch((e: ApiError) => {
          if (isDeletedOnServer(e)) {
            syncConfirm(individ);
          } else {
            syncReject(individ, e);
          }
        }),
    );
  }

  for (const individ of toDelete) {
    deleteRequests.push(
      jaktdataApi
        .deleteIndivid(individ.data.Id)
        .then(() => syncConfirm(individ))
        .catch((e: ApiError) => {
          if (isDeletedOnServer(e)) {
            syncConfirm(individ);
          } else {
            syncReject(individ, e);
          }
        }),
    );
  }

  return Promise.allSettled(deleteRequests)
    .then(() => {
      return Promise.allSettled(updateRequests);
    })
    .then(() => {
      return Promise.allSettled(createRequests);
    })
    .then(() => {
      pendingIndivider
        .filter((individ) => individ.operation === "none")
        .forEach((individ) => storage.delete({ id: individ.data.Id }, "Individ"));
      return {
        individerSynced,
        individerNotSynced,
      };
    });
};

export type SyncIndividerResult = {
  individerSynced: number;
  individerNotSynced: number;
};

export const SyncIndividerResultDefaults: SyncIndividerResult = {
  individerSynced: 0,
  individerNotSynced: 0,
};
